import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { pdf, PDFDownloadLink } from '@react-pdf/renderer';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import pdfWorker from 'pdfjs-dist/build/pdf.worker.min.js?url';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';

// Mobile browsers can't render @react-pdf's PDFViewer (it's an <iframe> that the
// native PDF plugin ignores) so the preview shows blank. Instead we generate the
// PDF blob, rasterize every page to an image with pdf.js, and stack them — plus a
// reliable Download button. Use this ONLY inside <MobileView>; desktop keeps PDFViewer.
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

const MobilePdfView = ({ document: pdfDocument, fileName }) => {
    const [pages, setPages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    // Render once per mount — the document/data is fixed for a given view, and the
    // parent passes a fresh element each render which would otherwise loop forever.
    const ranRef = useRef(false);

    useEffect(() => {
        if (ranRef.current) return undefined;
        ranRef.current = true;
        let alive = true;
        (async () => {
            try {
                const blob = await pdf(pdfDocument).toBlob();
                const buf = await blob.arrayBuffer();
                const doc = await pdfjsLib.getDocument({ data: buf }).promise;
                const imgs = [];
                for (let i = 1; i <= doc.numPages; i += 1) {
                    // eslint-disable-next-line no-await-in-loop
                    const page = await doc.getPage(i);
                    const viewport = page.getViewport({ scale: 2 }); // crisp on hi-dpi phones
                    const canvas = window.document.createElement('canvas');
                    canvas.width = viewport.width;
                    canvas.height = viewport.height;
                    const ctx = canvas.getContext('2d');
                    // eslint-disable-next-line no-await-in-loop
                    await page.render({ canvasContext: ctx, viewport }).promise;
                    imgs.push(canvas.toDataURL('image/jpeg', 0.85));
                }
                if (alive) setPages(imgs);
            } catch (e) {
                if (alive) setError(e?.message || 'Cannot render PDF');
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const base = (fileName || 'document').toString();
    const safeName = base.toLowerCase().endsWith('.pdf') ? base : `${base}.pdf`;

    return (
        <Stack spacing={1.2} sx={{ width: '100%', alignItems: 'center', py: 1 }}>
            <PDFDownloadLink document={pdfDocument} fileName={safeName} style={{ textDecoration: 'none', width: '100%' }}>
                {({ loading: dlLoading }) => (
                    <Button fullWidth variant="contained" startIcon={<DownloadIcon />} disabled={dlLoading} sx={{ bgcolor: '#1976d2' }}>
                        {dlLoading ? 'ກຳລັງກຽມໄຟລ໌…' : 'ດາວໂຫລດ PDF'}
                    </Button>
                )}
            </PDFDownloadLink>

            {loading && (
                <Stack alignItems="center" spacing={1} sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                    <Typography variant="caption" color="text.secondary">
                        ກຳລັງສ້າງຮູບ PDF…
                    </Typography>
                </Stack>
            )}
            {!!error && (
                <Typography variant="body2" color="error" sx={{ py: 2 }}>
                    ບໍ່ສາມາດສະແດງ PDF ໄດ້ ({error}) — ກະລຸນາກົດດາວໂຫລດ
                </Typography>
            )}
            {pages.map((src, i) => (
                <Box
                    // eslint-disable-next-line react/no-array-index-key
                    key={i}
                    component="img"
                    src={src}
                    alt={`page-${i + 1}`}
                    sx={{ width: '100%', borderRadius: 1, boxShadow: 1, border: '1px solid', borderColor: 'divider' }}
                />
            ))}
        </Stack>
    );
};

MobilePdfView.propTypes = {
    document: PropTypes.node,
    fileName: PropTypes.string
};

export default MobilePdfView;
